import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { PeriodSpec } from '@contracts';
import type { ProcurementScope } from '@contracts/procurement';
import { getCompanies, isSuperAdmin } from '@/services/authService';

/**
 * Filtros del dominio Compras/Gastos. AISLADO del `filterContext` de ventas: sus
 * dimensiones (estatus de seguimiento, urgencia, departamento) no se solapan con
 * las de POS. El scope real lo impone el BFF; aquí solo vive la selección de UI.
 */
interface ProcurementFilterState {
  /** `null` = todo el histórico (default del tablero operativo). */
  period: PeriodSpec | null;
  companies: string[];
  scope: ProcurementScope;
  setPeriod: (p: PeriodSpec | null) => void;
  setCompanies: (c: string[]) => void;
  setScope: (s: ProcurementScope) => void;
  /** Merge parcial del scope (cómodo para los KPIs clicables). */
  patchScope: (s: Partial<ProcurementScope>) => void;
}

const ProcurementContext = createContext<ProcurementFilterState | null>(null);

/** Compras vive en Odoo: VG/Pielé (pos_slug) + `corp` = la matriz S&G, que
 *  compra SIN tener POS (sin slug). SmartBamboo (Metabase) queda fuera del MVP. */
const ODOO_SLUGS = ['vg', 'pi', 'corp'];

export function ProcurementProvider({ children }: { children: ReactNode }) {
  const [period, setPeriod] = useState<PeriodSpec | null>(null);
  const [companies, setCompanies] = useState<string[]>(
    isSuperAdmin()
      ? ODOO_SLUGS
      : [
          ...getCompanies()
            .map((c) => c.slug)
            .filter((s) => ODOO_SLUGS.includes(s)),
          'corp', // la matriz no viene en el JWT (sin slug); staff la ve siempre
        ],
  );
  const [scope, setScope] = useState<ProcurementScope>({});

  const value = useMemo<ProcurementFilterState>(
    () => ({
      period,
      companies,
      scope,
      setPeriod,
      setCompanies,
      setScope,
      patchScope: (partial) => setScope((prev) => ({ ...prev, ...partial })),
    }),
    [period, companies, scope],
  );

  return <ProcurementContext.Provider value={value}>{children}</ProcurementContext.Provider>;
}

export function useProcurementFilters(): ProcurementFilterState {
  const ctx = useContext(ProcurementContext);
  if (!ctx) throw new Error('useProcurementFilters fuera de ProcurementProvider');
  return ctx;
}
